import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, Select, useToast } from '../components/ui';

interface Channel {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  categoryId: string | null;
  route: string | null;
  sortOrder: number;
  isActive: boolean;
  category: { id: string; slug: string; path: string; sector: string } | null;
}
interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

/** v2 §18 — the horizontal strip above the home feed. */
export function ChannelsPage() {
  const [editing, setEditing] = useState<{ row?: Channel } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api.get<Channel[]>('/channels') });
  const categories = useQuery({
    queryKey: ['categories-tree'],
    queryFn: () => api.get<CategoryNode[]>('/categories?tree=true'),
  });

  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? api.patch(`/channels/${id}`, body) : api.post('/channels', body),
    onSuccess: () => {
      toast('Channel saved', 'success');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const flatten = (nodes: CategoryNode[], depth = 0): Array<{ value: string; label: string }> =>
    nodes.flatMap((n) => [
      { value: n.id, label: `${'— '.repeat(depth)}${n.name}` },
      ...(n.children?.length ? flatten(n.children, depth + 1) : []),
    ]);

  return (
    <div className="stack">
      <Notice tone="info" title="Channels are not the same as categories">
        The strip includes entries that are not listing categories at all — “Globaxis Partners” opens a route rather
        than a feed. A channel therefore points at either a category or an in-app route.
      </Notice>

      <Card flush>
        <CardHead
          title="Channel strip"
          sub="Shown in order, above the home feed"
          action={
            <Button variant="primary" size="sm" onClick={() => setEditing({})}>
              + New channel
            </Button>
          }
        />
        {channels.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 120 }} />
          </div>
        ) : !channels.data?.length ? (
          <Empty icon="⌬" title="No channels" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Order</th>
                  <th>Name</th>
                  <th>Opens</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {channels.data.map((c) => (
                  <tr key={c.id}>
                    <td className="cell-num">{c.sortOrder}</td>
                    <td className="cell-strong">{c.name}</td>
                    <td>
                      {c.category ? (
                        <span className="cell-mono">{c.category.path}</span>
                      ) : c.route ? (
                        <Badge tone="purple">route {c.route}</Badge>
                      ) : (
                        <span className="cell-muted">nothing</span>
                      )}
                    </td>
                    <td>
                      <Badge tone={c.isActive ? 'green' : 'neutral'}>{c.isActive ? 'Visible' : 'Hidden'}</Badge>
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Button size="sm" onClick={() => setEditing({ row: c })}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ChannelEditor
          row={editing.row}
          categoryOptions={categories.data ? flatten(categories.data) : []}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(body) => save.mutate({ id: editing.row?.id, body })}
        />
      )}
    </div>
  );
}

function ChannelEditor({
  row,
  categoryOptions,
  onClose,
  onSave,
  saving,
}: {
  row?: Channel;
  categoryOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    name: row?.name ?? '',
    slug: row?.slug ?? '',
    target: row?.categoryId ? 'category' : 'route',
    categoryId: row?.categoryId ?? '',
    route: row?.route ?? '',
    sortOrder: String(row?.sortOrder ?? 0),
    isActive: row?.isActive === false ? 'false' : 'true',
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  function submit() {
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive === 'true',
      categoryId: form.target === 'category' ? form.categoryId || null : null,
      route: form.target === 'route' ? form.route.trim() || null : null,
    };
    if (!row) {
      body.slug =
        form.slug.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    onSave(body);
  }

  return (
    <Modal
      title={row ? `Edit “${row.name}”` : 'New channel'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!form.name.trim()} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <Input value={form.name} autoFocus onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Opens">
          <Select
            value={form.target}
            onChange={(e) => set('target', e.target.value)}
            options={[
              { value: 'category', label: 'A category feed' },
              { value: 'route', label: 'An in-app route' },
            ]}
          />
        </Field>
        {form.target === 'category' ? (
          <Field label="Category">
            <Select
              value={form.categoryId}
              placeholder="Choose…"
              onChange={(e) => set('categoryId', e.target.value)}
              options={categoryOptions}
            />
          </Field>
        ) : (
          <Field label="Route" hint="e.g. /partners">
            <Input value={form.route} onChange={(e) => set('route', e.target.value)} placeholder="/partners" />
          </Field>
        )}
        <div className="row">
          <Field label="Sort order">
            <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select
              value={form.isActive}
              onChange={(e) => set('isActive', e.target.value)}
              options={[
                { value: 'true', label: 'Visible' },
                { value: 'false', label: 'Hidden' },
              ]}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
